import { useMemo } from 'react';
import { usePermissionsContext } from './usePermissionContext.tsx';
import type { PermissionCheckResult } from './usePermission.ts';
import { SERVER_POLICY_ROLES, type ServerPolicyName } from './serverPolicies.generated.ts';

/**
 * Ask the question the SERVER actually answers: does this principal hold one of
 * the roles on the named authorization policy that gates the endpoint?
 *
 * WHY THIS EXISTS ALONGSIDE `usePermission`
 * ─────────────────────────────────────────
 * `usePermission(resource, action)` matches a `"resource:action"` string against
 * `permissions[]` from `GET /api/v1/auth/me`, which the gateway builds from the
 * `Permissions` table. Exactly one code path in the repository ever writes a row
 * to that table — `StartupProvisioning.SeedDevAdminPermissionAsync`, which
 * inserts a single `("*","*")` wildcard for the dev tenant
 * `00000000-0000-0000-0000-000000000001`. No migration seeds it, no endpoint
 * writes it, and `DefaultPermissionMatrixSeeder` feeds a different, in-memory
 * matrix used only by the Admin policy simulator.
 *
 * The consequence is not "some strings are missing". It is that a permission
 * string has two states and only two: everything passes (dev tenant, via the
 * wildcard) or everything fails (any other tenant, forever, for every principal
 * including a tenant admin). A control gated that way is not access control; it
 * is a control that is always off in production, showing the operator a denial
 * reason no grant can satisfy.
 *
 * Endpoints, meanwhile, are gated by NAMED ROLE POLICIES
 * (`.RequireAuthorization(PolicyNames.LimsManager)` →
 * `RequireRole("Admin", "LimsManager", "QualityManager", "Manager")`). Mirroring
 * that policy is the only UI check whose answer tracks what the server will do.
 *
 * @param policy A policy name from the generated mirror of
 *   `GatewayAuthorizationPolicies.cs`. The type only admits policies the gateway
 *   actually registers, and `scripts/generate-ui-server-policies.mjs --check`
 *   fails the build if a role list drifts.
 *
 * @example
 * // The endpoint is `.RequireAuthorization(PolicyNames.LimsManager)`.
 * const { allowed, loading } = useServerPolicy('Lims.Manager');
 * <Button disabled={!allowed || loading}>Dispose sample</Button>
 */
export const useServerPolicy = (policy: ServerPolicyName): PermissionCheckResult => {
  const { roles, loading, error, isWildcard } = usePermissionsContext();

  return useMemo<PermissionCheckResult>(() => {
    if (loading) return { allowed: false, loading: true, error, resolved: false };

    // Dev-token sessions carry `roles: ['*']` and mirror the gateway's dev
    // bypass. Without this a local flow would gate every control, which is the
    // failure that made the wildcard fallback necessary in the first place.
    if (isWildcard || roles.includes('*')) {
      return { allowed: true, loading: false, error, resolved: error === undefined };
    }

    const required = SERVER_POLICY_ROLES[policy] as readonly string[];

    // An empty role list is `RequireAuthenticatedUser()` with no role
    // requirement, so every signed-in principal satisfies it. `roles` is empty
    // for a signed-out session, and `PermissionsProvider` reports
    // `loading: false` with empty sets in that case — so require at least one
    // resolved role rather than treating "no session" as "authenticated".
    if (required.length === 0) {
      return { allowed: roles.length > 0, loading: false, error, resolved: error === undefined };
    }

    // `RequireRole(a, b, c)` is satisfied by ANY of the listed roles. Role names
    // are compared case-insensitively, matching the gateway's role claim
    // handling (a native-auth principal emits `role: "Admin"`, Keycloak emits a
    // `roles` array, and neither normalises case).
    const held = new Set(roles.map((r) => r.toLowerCase()));
    return {
      allowed: required.some((r) => held.has(r.toLowerCase())),
      loading: false,
      error,
      resolved: error === undefined,
    };
  }, [policy, roles, loading, error, isWildcard]);
};

/**
 * The role list a policy requires, for rendering an honest denial reason
 * ("reserved to Admin / LimsManager / QualityManager"). Empty means the policy
 * requires only an authenticated principal.
 */
export const serverPolicyRoles = (policy: ServerPolicyName): readonly string[] =>
  SERVER_POLICY_ROLES[policy] as readonly string[];
